import { routes } from 'app/routing/routesConfig';
import { ContextMenuButton, Td, Tr } from 'app/components/Table';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { dateFormats } from 'app/constants';
import { memo, MouseEvent } from 'react';
import { Release, TuneCoreRelease } from 'types/releases';
import {
  ListItemText,
  ListItemTextProps,
} from 'app/components/ChakraTableList';
import { ReleaseCover } from '../ReleaseCover';
import {
  getLatestExportedReleaseLabel,
  getLatestExportedTuneCoreReleaseLabel,
} from './utils';
import { getReleaseTypeName } from '../utils';

interface ReleaseRowProps {
  release: Release;
  contextHandler: (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
}

const ReleaseRowComponent = ({ release, contextHandler }: ReleaseRowProps) => {
  const navigate = useNavigate();
  const href = routes.release.path.replace(':guid', release.guid);

  const lastExportedTime =
    'latestExport' in release
      ? release.latestExport?.updated
      : release.latestExportShipment?.updated;

  const hasLatestExport =
    'latestExport' in release
      ? Object.keys(release.latestExport || {}).length !== 0
      : Object.keys(release.latestExportShipment || {}).length !== 0;

  const secondLabel = getLatestExportedReleaseLabel({
    shipment: release.latestExportShipment,
  });

  const getExportedColumn = () => {
    if (hasLatestExport && release.type === 'TuneCore') {
      return getLatestExportedTuneCoreReleaseLabel(release as TuneCoreRelease);
    }
    if (hasLatestExport) {
      return {
        secondary: secondLabel,
        secondaryTextProps: {
          fontSize: 'xs',
          'data-testid': 'export-recipient',
          color: 'textSubdued',
        },
      } as ListItemTextProps;
    }

    return {};
  };

  return (
    <Tr
      zIndex={1}
      position="relative"
      role="row"
      clickable
      onContextMenu={contextHandler}
      onClick={() => {
        navigate(href);
      }}
    >
      <Td isAvatar sx={{ overflow: 'visible' }}>
        <ReleaseCover size="small" type={release.type} />
      </Td>
      <Td data-testid="title" sx={{ pl: 0 }}>
        {release.title}
      </Td>
      <Td data-testid="modified">
        {format(new Date(release.updated.iso), dateFormats.LIST_FORMAT)}
      </Td>
      <Td data-testid="exported">
        <ListItemText
          data-testid="exported"
          primary={
            lastExportedTime
              ? format(new Date(lastExportedTime.iso), dateFormats.LIST_FORMAT)
              : '-'
          }
          {...getExportedColumn()}
        />
      </Td>
      <Td data-testid="release-type">{getReleaseTypeName(release.type)}</Td>
      <Td className="gap" />
      <Td>
        <ContextMenuButton onClick={contextHandler} />
      </Td>
    </Tr>
  );
};

export const ReleaseRow = memo(
  ReleaseRowComponent,
  (prev: ReleaseRowProps, curr: ReleaseRowProps) => {
    const compare = (prev: any, curr: any) =>
      JSON.stringify(prev) === JSON.stringify(curr);

    return (
      compare(prev.release.guid, curr.release.guid) &&
      compare(
        prev.release.latestExportShipment?.exportShipmentGuid,
        curr.release.latestExportShipment?.exportShipmentGuid
      ) &&
      compare(
        prev.release.latestExportShipment?.labelId,
        curr.release.latestExportShipment?.labelId
      )
    );
  }
);
